import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SocialPost, SocialPostFilters } from '@/types/team-posts';

export interface PostFilters {
  category?: string[];
  profile?: string[];
  dateRange?: string;
}

export function useTeamPosts(filters?: SocialPostFilters) {
  return useQuery({
    queryKey: ['team-posts', filters],
    queryFn: async () => {
      let query = (supabase as any)
        .from('v_social_posts_ui_enhanced')
        .select('*')
        .not('post_date', 'is', null)
        .order('post_date', { ascending: false });
      
      if (filters?.poster) {
        query = query.eq('poster_name', filters.poster);
      }
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.search) {
        query = query.ilike('full_content', `%${filters.search}%`);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as SocialPost[];
    }
  });
}

export function usePostsTable(filters: PostFilters, search: string, page: number = 1) {
  const pageSize = 50;
  
  return useQuery({
    queryKey: ['team-posts-table', filters, search, page],
    queryFn: async () => {
      let query = (supabase as any)
        .from('v_social_posts_ui_enhanced')
        .select('*', { count: 'exact' })
        .not('post_date', 'is', null);
      
      if (search) {
        query = query.or(`full_content.ilike.%${search}%,poster_name.ilike.%${search}%`);
      }
      
      if (filters.profile?.length) {
        query = query.in('poster_name', filters.profile);
      }
      
      if (filters.category?.length) {
        query = query.in('category', filters.category);
      }
      
      if (filters.dateRange && filters.dateRange !== 'all') {
        const now = new Date();
        let daysAgo = 0;
        switch (filters.dateRange) {
          case '7d': daysAgo = 7; break;
          case '30d': daysAgo = 30; break;
          case '90d': daysAgo = 90; break;
        }
        if (daysAgo > 0) {
          const since = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000).toISOString();
          query = query.gte('post_date', since);
        }
      }
      
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.order('post_date', { ascending: false }).range(from, to);
      
      const { data, error, count } = await query;
      if (error) throw error;
      
      return {
        data: (data || []) as SocialPost[],
        count: count || 0,
        pageSize
      };
    }
  });
}
